import { apiClient } from './client';
import type {
  SchoolNetwork,
  SchoolNetworkDetail,
  SchoolNetworkType,
  AvailableSchool,
  AttachSchoolResponse,
  CreateSchoolNetworkData,
  UpdateSchoolNetworkData,
  SchoolNetworkResponse,
  NetworkMetrics,
} from '@/types/school-network';

export async function getSchoolNetworks(type?: SchoolNetworkType): Promise<SchoolNetwork[]> {
  const response = await apiClient.get<SchoolNetwork[]>('/api/school-networks', {
    params: type ? { type } : undefined,
  });
  return response.data;
}

export async function getSchoolNetwork(id: number): Promise<SchoolNetworkDetail> {
  const response = await apiClient.get<SchoolNetworkDetail>(`/api/school-networks/${id}`);
  return response.data;
}

export async function createSchoolNetwork(
  data: CreateSchoolNetworkData
): Promise<SchoolNetworkResponse> {
  const response = await apiClient.post<SchoolNetworkResponse>('/api/school-networks', data);
  return response.data;
}

export async function updateSchoolNetwork(
  id: number,
  data: UpdateSchoolNetworkData
): Promise<SchoolNetworkResponse> {
  const response = await apiClient.put<SchoolNetworkResponse>(`/api/school-networks/${id}`, data);
  return response.data;
}

export async function deleteSchoolNetwork(id: number): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>(`/api/school-networks/${id}`);
  return response.data;
}

/** Réseaux VIRTUELS uniquement : gestion en masse des écoles membres */
export async function updateNetworkSchools(
  id: number,
  schoolIds: number[]
): Promise<{ message: string; schoolCount: number }> {
  const response = await apiClient.put<{ message: string; schoolCount: number }>(
    `/api/school-networks/${id}/schools`,
    { schoolIds }
  );
  return response.data;
}

/**
 * Réseaux PHYSIQUES uniquement : rattachement unitaire et DÉFINITIF d'une
 * école (host propre + confirmation explicite côté serveur).
 */
export async function attachSchoolToNetwork(
  networkId: number,
  schoolId: number,
  host: string
): Promise<AttachSchoolResponse> {
  const response = await apiClient.post<AttachSchoolResponse>(
    `/api/school-networks/${networkId}/schools`,
    { schoolId, host, confirm: true }
  );
  return response.data;
}

/** Réseaux physiques : modifie le host d'une école membre */
export async function updateNetworkSchoolHost(
  networkId: number,
  schoolId: number,
  host: string
): Promise<{ message: string; host: string; warnings: string[] }> {
  const response = await apiClient.put<{ message: string; host: string; warnings: string[] }>(
    `/api/school-networks/${networkId}/schools/${schoolId}/host`,
    { host }
  );
  return response.data;
}

/** Réseaux physiques V1-legacy : crée le tenant group V2 manquant */
export async function initializeNetworkV2(
  networkId: number,
  host: string
): Promise<{ v2TenantGroupId: string; v2Host: string; warnings: string[]; message: string }> {
  const response = await apiClient.post<{
    v2TenantGroupId: string;
    v2Host: string;
    warnings: string[];
    message: string;
  }>(`/api/school-networks/${networkId}/initialize-v2`, { host });
  return response.data;
}

export async function getAvailableSchools(networkId: number): Promise<AvailableSchool[]> {
  const response = await apiClient.get<AvailableSchool[]>(
    `/api/school-networks/${networkId}/available-schools`
  );
  return response.data;
}

export async function getAllAvailableSchools(): Promise<AvailableSchool[]> {
  const response = await apiClient.get<AvailableSchool[]>('/api/school-networks/available-schools');
  return response.data;
}

export async function getNetworkMetrics(id: number): Promise<NetworkMetrics> {
  const response = await apiClient.get<NetworkMetrics>(`/api/school-networks/${id}/metrics`);
  return response.data;
}

/**
 * Réseaux physiques : upload d'une image de branding V2 (logo connexion /
 * favicon / couverture), stockée sur le réseau puis poussée dans les settings
 * du tenant group V2.
 */
export async function uploadNetworkFile(
  networkId: number,
  field: 'logoSignin' | 'favicon' | 'cover',
  file: File
): Promise<{ message: string; url: string; pushed: boolean; pushWarning: string | null }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<{
    message: string;
    url: string;
    pushed: boolean;
    pushWarning: string | null;
  }>(`/api/school-networks/${networkId}/upload/${field}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}
